/** Artikel / Lager: Bestand je Artikel (physisch / reserviert / verfügbar), Detail mit Journal und Korrektur. */
import { useEffect, useState } from 'react';
import { Link, Route, Routes, useNavigate, useParams, useSearchParams } from 'react-router';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { KORREKTUR_GRUENDE, ROUTEN, skuVorschlagIntern, type KorrekturGrund, type ProduktAbgleichErgebnis, type Route as RouteT } from '@storeos/contracts';
import { artikelAendern, artikelAnlegen, artikelDetail, artikelListe, korrekturBuchen, lagerplaetze, produkteAbgleichen } from '../api/lager';
import { onDemandDruckSetzen } from '../api/produktion-erfassen';
import { fachEtikettDrucken } from '../components/code128';
import { BEWEGUNG_TEXT, datumZeit, KORREKTUR_TEXT, ROUTE_QUELLE_TEXT, ROUTE_TEXT, STEUERKLASSE_TEXT } from '../components/format';
import { RouteBadge } from '../components/Status';
import { Darf, Detailpanel, Dialog, DialogFormular, Feld, Filterleiste, Seitenkopf, Tabelle, useDarf, useToast, Zustand } from '../components/ui';

export function Artikel() {
  return (
    <Routes>
      <Route index element={<ArtikelSeite />} />
      <Route path=":id" element={<ArtikelSeite />} />
    </Routes>
  );
}

function ArtikelSeite() {
  const { id } = useParams();
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const f = Object.fromEntries(sp.entries()) as Record<string, string>;
  const [suche, setSuche] = useState(f.q ?? '');
  const q = useQuery({ queryKey: ['artikel', f], queryFn: () => artikelListe({ ...f, pro_seite: 500 }), placeholderData: keepPreviousData });
  const setze = (k: string, v: string) => { const n = new URLSearchParams(sp); if (v) n.set(k, v); else n.delete(k); setSp(n, { replace: true }); };
  const zeilen = q.data?.eintraege ?? [];
  const verborgen = zeilen.some((z) => z.bestand_verborgen);
  const sumVerf = zeilen.filter((z) => z.bestand_fuehren).reduce((s, z) => s + (z.verfuegbar ?? 0), 0);
  const zahl = (z: { bestand_fuehren: boolean }, v: number | null) => (verborgen ? <span className="hinweis">verborgen</span> : z.bestand_fuehren ? v : '–');
  // R127: Mehrfachauswahl für das Flag „Produktion on demand“ (Leitung/Admin)
  const darf = useDarf();
  const qc = useQueryClient();
  const toast = useToast();
  const [wahl, setWahl] = useState<Set<string>>(new Set());
  const umschalten = (aid: string) => setWahl((w) => { const n = new Set(w); if (n.has(aid)) n.delete(aid); else n.add(aid); return n; });
  async function onDemandSetzen(wert: boolean) {
    try {
      const r = await onDemandDruckSetzen([...wahl], wert);
      toast(`${r.geaendert} Artikel ${wert ? 'für die Produktion freigegeben' : 'nicht mehr für die Produktion freigegeben'}`);
      setWahl(new Set());
      void qc.invalidateQueries({ queryKey: ['artikel'] });
      void qc.invalidateQueries({ queryKey: ['produktion-erfassen'] });
    } catch (e) { toast((e as Error).message, 'fehler'); }
  }
  const wahlSpalte = darf('leitung') ? [{
    kopf: 'Wahl', klasse: 'nebensache',
    zelle: (z: { id: string; sku: string }) => <input type="checkbox" aria-label={`${z.sku} wählen`} checked={wahl.has(z.id)} onClick={(e) => e.stopPropagation()} onChange={() => umschalten(z.id)} data-testid="artikel-wahl" />,
  }] : [];

  return (
    <section className={id ? 'mit-panel' : ''}>
      <div className="hauptbereich">
        <Seitenkopf titel="Artikel / Lager" unter={q.data ? (verborgen ? `${q.data.gesamt} Artikel · Inventur läuft: Bestände sind bis zur Freigabe verborgen (Blindzählung)` : `${q.data.gesamt} Artikel · ${sumVerf} Einheiten verfügbar (nur Lagerware)`) : undefined}>
          <Darf rolle="leitung"><InternerArtikelKnopf /></Darf>
          <Darf rolle="admin"><ProduktAbgleichKnopf /></Darf>
        </Seitenkopf>
        <Filterleiste>
          <form className="suche" onSubmit={(e) => { e.preventDefault(); setze('q', suche.trim()); }}>
            <input type="search" value={suche} onChange={(e) => setSuche(e.target.value)} placeholder="SKU, Name, Variante oder EAN" aria-label="Artikelsuche" />
            <button type="submit" className="knopf">Suchen</button>
          </form>
          <select aria-label="Route" value={f.route ?? ''} onChange={(e) => setze('route', e.target.value)}>
            <option value="">Route: alle</option>
            {ROUTEN.map((r) => <option key={r} value={r}>{ROUTE_TEXT[r]}</option>)}
          </select>
          <label className="inline"><input type="checkbox" checked={!!f.nachbestellen} onChange={(e) => setze('nachbestellen', e.target.checked ? '1' : '')} /> nur unter Meldebestand</label>
          <label className="inline"><input type="checkbox" checked={!!f.on_demand_druck} onChange={(e) => setze('on_demand_druck', e.target.checked ? '1' : '')} data-testid="filter-on-demand" /> nur Produktion on demand</label>
        </Filterleiste>
        {darf('leitung') && wahl.size > 0 && (
          <div className="hinweis-box zeile" data-testid="artikel-wahl-leiste">
            <strong>{wahl.size} gewählt</strong>
            <button type="button" className="knopf klein" onClick={() => void onDemandSetzen(true)} data-testid="on-demand-an">Für Produktion freigeben</button>
            <button type="button" className="knopf-zweit klein" onClick={() => void onDemandSetzen(false)} data-testid="on-demand-aus">Freigabe entfernen</button>
            <button type="button" className="knopf-flach" onClick={() => setWahl(new Set())}>Auswahl leeren</button>
          </div>
        )}
        <Zustand laden={q.isLoading} fehler={q.error}>
          <Tabelle testid="artikel-tabelle" zeilen={zeilen} schluessel={(z) => z.id} aktiv={id} onZeile={(z) => nav(`/artikel/${z.id}${sp.toString() ? `?${sp}` : ''}`)}
            spalten={[
              ...wahlSpalte,
              { kopf: 'SKU', zelle: (z) => z.sku, klasse: 'mono' },
              { kopf: 'Artikel', zelle: (z) => <span className="kuerzen">{z.name}{z.variante && <small className="hinweis"> · {z.variante}</small>}{z.gewicht_unbekannt && <small className="warn-text" data-testid="gewicht-unbekannt"> · Gewicht unbekannt</small>}{z.on_demand_druck && <small className="hinweis" data-testid="on-demand-marke"> · Produktion on demand</small>}</span> },
              { kopf: 'Route', zelle: (z) => <RouteBadge route={z.route} />, klasse: 'nebensache' },
              { kopf: 'Platz', zelle: (z) => z.bin_code ?? '–', klasse: 'mono nebensache' },
              { kopf: 'Physisch', zelle: (z) => zahl(z, z.physisch), rechts: true },
              { kopf: 'Reserviert', zelle: (z) => (verborgen ? zahl(z, null) : z.bestand_fuehren || z.reserviert ? z.reserviert : '–'), rechts: true },
              { kopf: 'Verfügbar', zelle: (z) => (verborgen || !z.bestand_fuehren || z.verfuegbar === null ? zahl(z, null) : <strong className={z.verfuegbar < 0 ? 'negativ' : z.meldebestand !== null && z.verfuegbar < z.meldebestand ? 'warn-text' : ''}>{z.verfuegbar}</strong>), rechts: true },
              { kopf: 'Melde', zelle: (z) => z.meldebestand ?? '–', rechts: true },
            ]} />
        </Zustand>
      </div>
      {id && <ArtikelPanel id={id} onSchliessen={() => nav(`/artikel${sp.toString() ? `?${sp}` : ''}`)} />}
    </section>
  );
}

function ArtikelPanel({ id, onSchliessen }: { id: string; onSchliessen: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const darf = useDarf();
  const q = useQuery({ queryKey: ['artikel', 'detail', id], queryFn: () => artikelDetail(id) });
  const bins = useQuery({ queryKey: ['lagerplaetze'], queryFn: lagerplaetze, staleTime: 300_000 });
  const [korrektur, setKorrektur] = useState(false);
  const [melde, setMelde] = useState('');
  const [gramm, setGramm] = useState('');
  const d = q.data;
  useEffect(() => { if (d) { setMelde(d.meldebestand === null ? '' : String(d.meldebestand)); setGramm(d.gewicht_g === null ? '' : String(d.gewicht_g)); } }, [d]);
  const neu = () => { void qc.invalidateQueries({ queryKey: ['artikel'] }); void qc.invalidateQueries({ queryKey: ['dashboard'] }); };
  async function aendern(daten: Parameters<typeof artikelAendern>[1]) {
    try { await artikelAendern(id, daten); toast('Gespeichert'); neu(); } catch (e) { toast((e as Error).message, 'fehler'); }
  }
  const platzCode = (bid: string | null) => bins.data?.eintraege.find((b) => b.id === bid)?.code ?? null;

  return (
    <Detailpanel titel={d ? <><span className="mono">{d.sku}</span></> : 'Artikel'} onSchliessen={onSchliessen}>
      <Zustand laden={q.isLoading} fehler={q.error}>
        {d && (
          <>
            <p className="panel-titel">{d.name}{d.variante && <> · {d.variante}</>}</p>
            {d.bestand_verborgen ? (
              <p className="warn-box" data-testid="artikel-bestand">Inventur läuft: Bestand und Journal sind bis zur Freigabe verborgen (Blindzählung).</p>
            ) : (
              <div className="bestand-zahlen" data-testid="artikel-bestand">
                <div><span>Physisch</span><strong>{d.physisch}</strong></div>
                <div><span>Reserviert</span><strong>{d.reserviert}</strong></div>
                <div><span>Verfügbar</span><strong className={(d.verfuegbar ?? 0) < 0 ? 'negativ' : ''} data-testid="artikel-verfuegbar">{d.verfuegbar}</strong></div>
              </div>
            )}
            <dl className="werte">
              <dt>EAN</dt><dd className="mono">{d.ean ?? '– (internes Etikett)'}</dd>
              <dt>Steuer</dt><dd>{STEUERKLASSE_TEXT[d.steuerklasse] ?? d.steuerklasse}</dd>
              <dt>Gewicht</dt><dd data-testid="artikel-gewicht">{d.gewicht_g === null ? <span className="warn-text">unbekannt – an der Packstation wiegen</span> : `${d.gewicht_g} g`}</dd>
              <dt>Maße</dt><dd>{d.laenge_mm ?? '–'} × {d.breite_mm ?? '–'} × {d.hoehe_mm ?? '–'} mm</dd>
              <dt>Bestand</dt><dd>{d.bestand_fuehren ? 'wird geführt' : 'nicht geführt'}</dd>
              {d.listings.length === 0 && d.sku.startsWith('NG-INT-') && <><dt>Herkunft</dt><dd data-testid="artikel-intern">intern (nur StoreOS, kein Shop-Listing)</dd></>}
            </dl>
            <KomponenteBlock artikel={d} onAendern={aendern} />
            <div className="formular kompakt">
              <Feld label="Route">
                <span className="zeile">
                  <select value={d.route} disabled={!darf('leitung')} onChange={(e) => aendern({ route: e.target.value as RouteT })}>
                    {ROUTEN.map((r) => <option key={r} value={r}>{ROUTE_TEXT[r]}</option>)}
                  </select>
                  <small className="hinweis" data-testid="route-quelle">{ROUTE_QUELLE_TEXT[d.route_quelle] ?? d.route_quelle}</small>
                </span>
              </Feld>
              <Feld label="Gewicht (g)">
                <span className="zeile">
                  <input type="number" min={1} value={gramm} disabled={!darf('leitung')} onChange={(e) => setGramm(e.target.value)} placeholder="unbekannt" style={{ width: '6rem' }} />
                  <Darf rolle="leitung"><button type="button" className="knopf-zweit klein" onClick={() => aendern({ gewicht_g: gramm === '' ? null : Number(gramm) })}>Speichern</button></Darf>
                </span>
              </Feld>
              <Feld label="Produktion on demand" hinweis="B&B darf diesen Artikel unter „Produktion erfassen“ als Zugang buchen.">
                <span className="zeile">
                  <input type="checkbox" checked={!!d.on_demand_druck} disabled={!darf('leitung')} onChange={(e) => aendern({ on_demand_druck: e.target.checked })} data-testid="artikel-on-demand" />
                  <span>{d.on_demand_druck ? 'freigegeben' : 'nicht freigegeben'}</span>
                </span>
              </Feld>
              <Feld label="Lagerplatz">
                <select value={d.standard_bin_id ?? ''} disabled={!darf('leitung')} onChange={(e) => aendern({ standard_bin_id: e.target.value || null })}>
                  <option value="">– kein Platz –</option>
                  {bins.data?.eintraege.map((b) => <option key={b.id} value={b.id}>{b.code}</option>)}
                </select>
              </Feld>
              <Feld label="Meldebestand">
                <span className="zeile">
                  <input type="number" min={0} value={melde} disabled={!darf('leitung')} onChange={(e) => setMelde(e.target.value)} style={{ width: '6rem' }} />
                  <Darf rolle="leitung"><button type="button" className="knopf-zweit klein" onClick={() => aendern({ meldebestand: melde === '' ? null : Number(melde) })}>Speichern</button></Darf>
                </span>
              </Feld>
            </div>
            <div className="knopfreihe links">
              <Darf rolle="leitung"><button type="button" className="knopf" onClick={() => setKorrektur(true)} data-testid="korrektur-oeffnen">Korrekturbuchung</button></Darf>
              <button type="button" className="knopf-zweit" onClick={() => fachEtikettDrucken({ sku: d.sku, name: d.name, variante: d.variante, platz: platzCode(d.standard_bin_id) })}>Fach-Etikett drucken</button>
            </div>
            {d.plaetze.length > 0 && (
              <>
                <h3>Je Platz</h3>
                <ul className="liste-einfach">{d.plaetze.map((p) => <li key={p.bin_id}><span className="mono">{p.code}</span><span className="rechts">{p.menge}</span></li>)}</ul>
              </>
            )}
            {d.reservierungen.length > 0 && (
              <>
                <h3>Reserviert für ({d.reservierungen.length})</h3>
                <ul className="liste-einfach">{d.reservierungen.map((x) => <li key={x.id}><Link to={`/auftraege/${x.order_id}`} className="mono">{x.bestellnummer}</Link><span className="hinweis">{datumZeit(x.created_at)}</span><span className="rechts">{x.menge}</span></li>)}</ul>
              </>
            )}
            {d.listings.length > 0 && (
              <>
                <h3>Kanal</h3>
                <ul className="liste-einfach">{d.listings.map((x) => <li key={x.id}><span className="mono">Woo {x.external_variation_id ?? x.external_product_id}</span><span>{x.bestand_sync ? 'Bestand-Sync an' : 'kein Sync'}</span><span className="hinweis">zuletzt gemeldet {x.letzter_push_wert ?? '–'}</span></li>)}</ul>
              </>
            )}
            <h3>Journal</h3>
            <Tabelle testid="journal" zeilen={d.journal} schluessel={(m) => m.id} leer="Keine Bewegungen"
              spalten={[
                { kopf: 'Zeit', zelle: (m) => datumZeit(m.created_at) },
                { kopf: 'Grund', zelle: (m) => <>{BEWEGUNG_TEXT[m.grund] ?? m.grund}{m.korrektur_grund && <small className="hinweis"> · {KORREKTUR_TEXT[m.korrektur_grund]}</small>}</> },
                { kopf: 'Platz', zelle: (m) => m.bin_code ?? '–', klasse: 'mono' },
                { kopf: 'Menge', zelle: (m) => <strong className={m.menge < 0 ? 'negativ' : 'positiv'}>{m.menge > 0 ? `+${m.menge}` : m.menge}</strong>, rechts: true },
                { kopf: 'Wer', zelle: (m) => m.benutzer ?? 'System' },
              ]} />
          </>
        )}
      </Zustand>
      <Dialog offen={korrektur} titel="Korrekturbuchung" onSchliessen={() => setKorrektur(false)}>
        {d && <KorrekturFormular id={id} binId={d.standard_bin_id} bins={bins.data?.eintraege ?? []} onAbbrechen={() => setKorrektur(false)} onFertig={() => { setKorrektur(false); toast('Korrektur gebucht'); neu(); }} />}
      </Dialog>
    </Detailpanel>
  );
}

/**
 * R110: Komponente (einstufige Stückliste) im Artikeldetail. Je verkauftem Stück wird `menge` × Komponente reserviert
 * und beim Tagesabschluss ausgebucht; offene Aufträge ziehen beim Speichern nach. Leitung/Admin.
 */
function KomponenteBlock({ artikel, onAendern }: {
  artikel: { id: string; komponente_article_id: string | null; komponente_menge: number | null; komponente?: { id: string; sku: string; name: string; variante: string | null } | null; verwendet_in?: Array<{ id: string; sku: string; name: string; komponente_menge: number | null }> };
  onAendern: (d: Parameters<typeof artikelAendern>[1]) => Promise<void>;
}) {
  const darf = useDarf();
  const [suche, setSuche] = useState('');
  const [wahl, setWahl] = useState<{ id: string; sku: string; name: string } | null>(null);
  const [menge, setMenge] = useState(String(artikel.komponente_menge ?? 1));
  useEffect(() => { setMenge(String(artikel.komponente_menge ?? 1)); setWahl(null); setSuche(''); }, [artikel.id, artikel.komponente_article_id, artikel.komponente_menge]);
  const treffer = useQuery({
    queryKey: ['artikel', 'komponente-suche', suche], enabled: suche.trim().length >= 2,
    queryFn: () => artikelListe({ q: suche.trim(), pro_seite: 8 }),
  });
  const k = artikel.komponente;
  const kandidaten = (treffer.data?.eintraege ?? []).filter((t) => t.id !== artikel.id && t.bestand_fuehren && !t.komponente_article_id);
  return (
    <div className="formular kompakt" data-testid="komponente">
      <h3>Komponente</h3>
      {k ? (
        <p data-testid="komponente-aktuell"><Link to={`/artikel/${k.id}`} className="mono">{k.sku}</Link> {k.name}{k.variante && <> · {k.variante}</>} · <strong>{artikel.komponente_menge ?? 1}×</strong> je Stück</p>
      ) : <p className="hinweis">Keine Komponente. Der Artikel reserviert {artikel.komponente_article_id ? '' : 'sich selbst (wenn er Bestand führt)'}.</p>}
      {darf('leitung') && (
        <>
          <Feld label={k ? 'Andere Komponente' : 'Komponente suchen'}>
            <input type="search" value={suche} onChange={(e) => { setSuche(e.target.value); setWahl(null); }} placeholder="SKU oder Name (mind. 2 Zeichen)" data-testid="komponente-suche" />
          </Feld>
          {suche.trim().length >= 2 && !wahl && (
            <ul className="liste-einfach" data-testid="komponente-treffer">
              {kandidaten.length === 0 && <li className="hinweis">Kein bestandsgeführter Artikel ohne eigene Komponente gefunden.</li>}
              {kandidaten.map((t) => (
                <li key={t.id}><button type="button" className="knopf-zweit klein" onClick={() => setWahl({ id: t.id, sku: t.sku, name: t.name })}><span className="mono">{t.sku}</span> {t.name}</button></li>
              ))}
            </ul>
          )}
          <Feld label="Menge je Stück">
            <span className="zeile">
              <input type="number" min={1} max={99} value={menge} onChange={(e) => setMenge(e.target.value)} style={{ width: '5rem' }} data-testid="komponente-menge" />
              {wahl && <small className="mono">{wahl.sku}</small>}
              <button type="button" className="knopf-zweit klein" disabled={!wahl && !k} data-testid="komponente-speichern"
                onClick={() => onAendern({ komponente_article_id: (wahl ?? k)!.id, komponente_menge: Number(menge) })}>Speichern</button>
              {k && <button type="button" className="knopf-zweit klein" data-testid="komponente-entfernen" onClick={() => onAendern({ komponente_article_id: null })}>Entfernen</button>}
            </span>
          </Feld>
          <p className="hinweis">Beim Speichern ziehen offene Aufträge ihre Reservierung nach (alte freigegeben, neue angelegt).</p>
        </>
      )}
      {(artikel.verwendet_in?.length ?? 0) > 0 && (
        <>
          <h3>Komponente von</h3>
          <ul className="liste-einfach">{artikel.verwendet_in!.map((v) => <li key={v.id}><Link to={`/artikel/${v.id}`} className="mono">{v.sku}</Link><span>{v.name}</span><span className="rechts">{v.komponente_menge ?? 1}×</span></li>)}</ul>
        </>
      )}
    </div>
  );
}

/** R109: interner Artikel ohne Kanal-Listing (z. B. Innenbuch als Komponente), Leitung/Admin. */
function InternerArtikelKnopf() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const toast = useToast();
  const bins = useQuery({ queryKey: ['lagerplaetze'], queryFn: lagerplaetze, staleTime: 300_000 });
  const [offen, setOffen] = useState(false);
  const [name, setName] = useState('');
  const [sku, setSku] = useState('');
  const [skuGetippt, setSkuGetippt] = useState(false);
  const [gramm, setGramm] = useState('');
  const [fuehren, setFuehren] = useState(true);
  const [bin, setBin] = useState('');
  const [steuer, setSteuer] = useState<'standard' | 'ermaessigt'>('standard');
  const vorschlag = skuVorschlagIntern(name);
  const zu = () => { setOffen(false); setName(''); setSku(''); setSkuGetippt(false); setGramm(''); setFuehren(true); setBin(''); setSteuer('standard'); };
  return (
    <>
      <button type="button" className="knopf-zweit" onClick={() => setOffen(true)} data-testid="intern-anlegen">Interner Artikel</button>
      <Dialog offen={offen} titel="Interner Artikel anlegen" onSchliessen={zu}>
        <DialogFormular absendenText="Anlegen" onAbbrechen={zu} onAbsenden={async () => {
          if (!name.trim()) throw new Error('Name ist Pflicht.');
          const g = gramm === '' ? null : Number(gramm);
          if (g !== null && (!Number.isInteger(g) || g <= 0)) throw new Error('Gewicht in ganzen Gramm (leer = unbekannt).');
          const a = await artikelAnlegen({ name: name.trim(), sku: (skuGetippt ? sku : vorschlag) || null, gewicht_g: g, bestand_fuehren: fuehren, standard_bin_id: bin || null, steuerklasse: steuer });
          toast(`${a.sku} angelegt`);
          void qc.invalidateQueries({ queryKey: ['artikel'] });
          zu();
          nav(`/artikel/${a.id}`);
        }}>
          <p className="hinweis">Nur in StoreOS, ohne Shop-Listing (z. B. Innenbuch als Komponente). Bestand 0; Bestand kommt über Wareneingang oder Inventur.</p>
          <Feld label="Name (Pflicht)"><input autoFocus value={name} onChange={(e) => setName(e.target.value)} data-testid="intern-name" /></Feld>
          <Feld label="SKU"><input className="mono" value={skuGetippt ? sku : vorschlag} onChange={(e) => { setSkuGetippt(true); setSku(e.target.value.toUpperCase()); }} data-testid="intern-sku" /></Feld>
          <Feld label="Gewicht (g)"><input type="number" min={1} value={gramm} onChange={(e) => setGramm(e.target.value)} placeholder="unbekannt" /></Feld>
          <Feld label="Lagerplatz"><select value={bin} onChange={(e) => setBin(e.target.value)}><option value="">– kein Platz –</option>{bins.data?.eintraege.map((b) => <option key={b.id} value={b.id}>{b.code}</option>)}</select></Feld>
          <Feld label="Steuer"><select value={steuer} onChange={(e) => setSteuer(e.target.value as 'standard' | 'ermaessigt')}><option value="standard">{STEUERKLASSE_TEXT.standard ?? 'standard'}</option><option value="ermaessigt">{STEUERKLASSE_TEXT.ermaessigt ?? 'ermäßigt'}</option></select></Feld>
          <label className="inline"><input type="checkbox" checked={fuehren} onChange={(e) => setFuehren(e.target.checked)} /> Bestand führen</label>
        </DialogFormular>
      </Dialog>
    </>
  );
}

/** R102: Artikelstamm aus Woo übernehmen (Admin). Zeigt das Ergebnis je Store und die neu geprüften Klärfälle. */
function ProduktAbgleichKnopf() {
  const qc = useQueryClient();
  const toast = useToast();
  const [laeuft, setLaeuft] = useState(false);
  const [erg, setErg] = useState<ProduktAbgleichErgebnis | null>(null);
  async function los() {
    setLaeuft(true);
    try {
      const r = await produkteAbgleichen();
      setErg(r);
      void qc.invalidateQueries({ queryKey: ['artikel'] });
      void qc.invalidateQueries({ queryKey: ['auftraege'] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
    } catch (e) { toast((e as Error).message, 'fehler'); } finally { setLaeuft(false); }
  }
  const zahl = (m: Record<string, number>) => Object.entries(m).map(([k, v]) => `${ROUTE_TEXT[k as RouteT] ?? ROUTE_QUELLE_TEXT[k as keyof typeof ROUTE_QUELLE_TEXT] ?? k} ${v}`).join(' · ') || '–';
  return (
    <>
      <button type="button" className="knopf-zweit" onClick={los} disabled={laeuft} data-testid="produkte-abgleichen">{laeuft ? 'Abgleich läuft …' : 'Artikel aus Woo übernehmen'}</button>
      <Dialog offen={erg !== null} titel="Produktabgleich mit Woo" onSchliessen={() => setErg(null)}>
        {erg && (
          <div data-testid="abgleich-ergebnis">
            {erg.stores.map((s) => (
              <dl className="werte" key={s.store_code}>
                <dt>Shop</dt><dd className="mono">{s.store_code}</dd>
                <dt>Einheiten im Shop</dt><dd>{s.produkte}</dd>
                <dt>Artikel neu</dt><dd data-testid="abgleich-neu">{s.artikel_neu}</dd>
                <dt>nach Route</dt><dd>{zahl(s.nach_route)}</dd>
                <dt>Route aus</dt><dd>{zahl(s.nach_route_quelle)}</dd>
                <dt>aktualisiert</dt><dd>{s.artikel_aktualisiert}</dd>
                <dt>ohne SKU</dt><dd className={s.ohne_sku ? 'warn-text' : ''} data-testid="abgleich-ohne-sku">{s.ohne_sku}{s.ohne_sku ? ' (nicht angelegt, Protokoll → Sync-Log)' : ''}</dd>
                <dt>nicht veröffentlicht</dt><dd>{s.inaktiv}</dd>
                <dt>Gewicht unbekannt</dt><dd>{s.gewicht_unbekannt}</dd>
                <dt>bestandsgeführt</dt><dd>{s.bestand_gefuehrt}</dd>
                <dt>Startbestand</dt><dd>{s.startbestand_summe} Stück{s.negativ_auf_null ? ` · ${s.negativ_auf_null} negativ → 0` : ''}{s.nach_umschaltung_null ? ` · ${s.nach_umschaltung_null} nach Umschaltung mit 0` : ''}</dd>
              </dl>
            ))}
            <p data-testid="abgleich-klaerfaelle">Klärfälle „Artikel unbekannt“: {erg.klaerfaelle.geprueft} geprüft, {erg.klaerfaelle.geloest} gelöst, {erg.klaerfaelle.offen} offen.</p>
            <p className="hinweis">Startbestand aus Woo gilt nur im Parallelbetrieb; die Erstzählung zur Umschaltung ersetzt ihn. Lagerplätze vergibt die Leitung.</p>
          </div>
        )}
      </Dialog>
    </>
  );
}

function KorrekturFormular({ id, binId, bins, onAbbrechen, onFertig }: { id: string; binId: string | null; bins: { id: string; code: string }[]; onAbbrechen: () => void; onFertig: () => void }) {
  const [menge, setMenge] = useState('');
  const [grund, setGrund] = useState<KorrekturGrund | ''>('');
  const [bin, setBin] = useState(binId ?? '');
  const [bemerkung, setBemerkung] = useState('');
  return (
    <DialogFormular absendenText="Buchen" onAbbrechen={onAbbrechen} onAbsenden={async () => {
      const n = Number(menge);
      if (!Number.isInteger(n) || n === 0) throw new Error('Menge (positiv oder negativ, ungleich 0) angeben.');
      if (!grund) throw new Error('Korrekturgrund ist Pflicht.');
      await korrekturBuchen(id, { menge: n, korrektur_grund: grund, bin_id: bin || null, bemerkung: bemerkung || undefined });
      onFertig();
    }}>
      <Feld label="Menge (± Stück)"><input autoFocus type="number" value={menge} onChange={(e) => setMenge(e.target.value)} placeholder="z. B. -2" /></Feld>
      <Feld label="Grund (Pflicht)">
        <select value={grund} onChange={(e) => setGrund(e.target.value as KorrekturGrund)}>
          <option value="">– bitte wählen –</option>
          {KORREKTUR_GRUENDE.map((g) => <option key={g} value={g}>{KORREKTUR_TEXT[g]}</option>)}
        </select>
      </Feld>
      <Feld label="Platz"><select value={bin} onChange={(e) => setBin(e.target.value)}><option value="">Standardplatz</option>{bins.map((b) => <option key={b.id} value={b.id}>{b.code}</option>)}</select></Feld>
      <Feld label="Bemerkung"><input value={bemerkung} onChange={(e) => setBemerkung(e.target.value)} /></Feld>
    </DialogFormular>
  );
}
